<template>
  <div
    class="form-input-box-wrap"
    :class="{ error: error, 'with-prefix': $slots.prefix, 'with-suffix': $slots.suffix }"
  >
    <div class="form-fixwrap form-input-prefix" v-if="$slots.prefix">
      <slot name="prefix"></slot>
    </div>
    <div
      class="form-input-wrap"
      :class="{
        'with-prefix': $slots.prefix,
        'with-suffix': $slots.suffix,
        'with-clear': clearable && inputValue,
      }"
    >
      <input
        ref="input"
        class="form-input"
        :type="type"
        :placeholder="placeholder"
        :disabled="inputDisabled"
        :readonly="readonly"
        :maxlength="maxlength"
        :minlength="minlength"
        :name="name"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
      />
      <div class="form-input-cancel" v-if="clearable && inputValue" @click="handleClear"></div>
    </div>
    <div class="form-fixwrap form-input-suffix" v-if="$slots.suffix">
      <slot name="suffix"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DdInput',

  inject: {
    elForm: {
      default: '',
    },
    elFormItem: {
      default: '',
    },
  },

  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    type: {
      type: String,
      default: 'text',
      validator: (value) =>
        ['text', 'password', 'number', 'email', 'tel', 'url', 'search'].includes(value),
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: Number,
      default: undefined,
    },
    minlength: {
      type: Number,
      default: undefined,
    },
    name: {
      type: String,
      default: '',
    },
    error: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    inputValue: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
        this.$emit('update:value', val)
      },
    },
    nativeInputValue() {
      return this.value === null || this.value === undefined ? '' : this.value
    },
    inputDisabled() {
      return this.$options.propsData.hasOwnProperty('disabled')
        ? this.disabled
        : (this.elForm || {}).disabled
    },
  },

  watch: {
    nativeInputValue() {
      this.setNativeInputValue()
    },
  },

  mounted() {
    this.setNativeInputValue()
  },

  methods: {
    handleInput(event) {
      const value = event.target.value
      this.$emit('input', value)

      this.$nextTick(this.setNativeInputValue)
    },
    handleFocus(event) {
      this.$emit('focus', event)
    },
    handleBlur(event) {
      this.$emit('blur', event)
    },
    handleChange(event) {
      this.$emit('change', event.target.value)
    },
    getInput() {
      return this.$refs.input
    },
    focus() {
      this.getInput().focus()
    },
    blur() {
      this.getInput().blur()
    },
    setValue(value) {
      this.getInput().value = value
    },
    handleClear() {
      this.$emit('input', '')
      this.$emit('change', '')
      this.$emit('clear')
      this.$nextTick(() => {
        this.getInput().focus()
      })
    },
    setNativeInputValue() {
      const input = this.getInput()
      if (!input) return
      if (input.value === this.nativeInputValue) return
      input.value = this.nativeInputValue
    },
  },
}
</script>

<style lang="less" scoped>
.form-input-box-wrap {
  width: 100%;
  overflow: hidden;
  position: relative;
  height: 44px;
  border: 1px solid rgba(77, 83, 88, 1);
  border-radius: 8px;
  font-size: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &.with-suffix {
    padding-right: 0;
  }

  &.error {
    border-color: #df3c3c;
  }

  .form-fixwrap {
    height: 19px;
    line-height: 19px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: rgba(0, 0, 0, 1);
    &.form-input-prefix {
      border-right: 1px solid #d9d9d9;
      min-width: 44px;
    }
    &.form-input-suffix {
      border-left: 1px solid #d9d9d9;
      padding: 0 10px;
      min-width: 80px;
    }
  }

  .form-input-wrap {
    flex: 1;
    padding: 0 12px;
    height: 100%;
    position: relative;
    &.with-prefix {
      padding-left: 8px;
    }
    &.with-suffix {
      padding-right: 8px;
    }
    &.with-clear {
      padding-right: 36px;
    }
  }
  .form-input {
    width: 100%;
    height: 100%;
    font-size: 16px;
    font-weight: 500;
    color: rgba(0, 0, 0, 1);
    caret-color: #08b058;
    border: none;
    outline: none;
    margin: 0;
    background: transparent;
    box-sizing: border-box;

    &::placeholder {
      color: rgba(162, 169, 176, 1);
      font-weight: 400;
      font-family: 'roboto-regular';
    }

    &:focus {
      outline: none;

      &::-webkit-contacts-auto-fill-button {
        opacity: 0;
      }
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    &[readonly] {
      cursor: default;
    }
  }

  .form-input-cancel {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    cursor: pointer;
    background: #a3a9b0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    &::before,
    &::after {
      content: '';
      position: absolute;
      width: 12px;
      height: 2px;
      background: #ffffff;
      border-radius: 1px;
    }

    &::before {
      transform: rotate(45deg);
    }

    &::after {
      transform: rotate(-45deg);
    }

    &:hover {
      background: rgba(77, 83, 88, 1);
    }
  }
}
</style>
